import { useState, useMemo, useEffect } from 'react'
import _ from 'lodash'

import {
  validateField,
  convertValue,
  isDirty,
  removeErrorsBlock,
  changeErrorsPath,
  swapErrors,
  correctErrorsAfterDeleteItem,
  copyErrors,
} from './utils'

const modifyUtils = {
  removeErrorsBlock,
  changeErrorsPath,
  swapErrors,
  correctErrorsAfterDeleteItem,
  copyErrors,
}

const useModel = initialState => {

  // used for dirty check to avoid JSON.stringify on each change
  const initialStateValuesJSON = useMemo(() => JSON.stringify(initialState.values), [initialState])

  const memoizedState = useMemo(() => {
    return {
      values: JSON.parse(initialStateValuesJSON),
      isValid: true,
      isDirty: initialState.isDirty,
    }
  }, [initialState.isDirty, initialStateValuesJSON])

  const [model, setModel] = useState(memoizedState)

  // update model on initial state changes
  useEffect(() => {
    setModel({
      values: JSON.parse(initialStateValuesJSON),
      isValid: true,
      isDirty: memoizedState.isDirty,
    })
  }, [initialStateValuesJSON, memoizedState.isDirty])

  const modelProps = useMemo(() => {

    const onChange = (e, modifier, callBack) => {
      const name = e.target.name
      const value = convertValue(e.target.value, e.target.type, e.target.getAttribute && e.target.getAttribute('data-integer') === 'true')
      const newModel = modifier && typeof modifier === 'function' ? modifier(model, modifyUtils) : model
      const result = {
        ...newModel,
      }
      if (value === undefined) {
        _.unset(result, name)
      } else {
        _.set(result, name, value)
      }
      result.isDirty = isDirty({ field: name, model: result, initialStateValuesJSON })
      if (modifier && typeof modifier === 'function') {
        result.isValid = Reflect.ownKeys(result.errors || {}).length === 0
      }
      setModel(result)
      callBack && callBack(result)
    }

    // not safe, validation is not triggered here, use to create or update valid values only
    const batchChange = newModel => {
      const result = {
        ...model,
        ...newModel,
      }
      result.isDirty = true
      setModel(result)
    }

    const reset = name => {
      const initial = _.get(initialState, name)
      _.set(model, name, JSON.parse(JSON.stringify(initial)))
      setModel({
        ...model,
        isDirty: false,
      })
    }

    const resetDirty = name => {
      setModel({
        ...model,
        isDirty: false,
      })
    }

    const isValidPath = path => {
      if (!model.errors) {
        return true
      } else {
        let valid = true
        Reflect.ownKeys(model.errors).forEach(key => {
          if(key.includes(path)) {
            valid = false
          }
        })
        return valid
      }
    }

    const cleanErrorsPath = path => {
      if (model.errors) {
        const toRemove = Reflect.ownKeys(model.errors).filter(key => key.includes(path))
        if (toRemove && toRemove.length > 0) {
          setModel({
            ...model,
            errors: _.omit(model.errors, toRemove),
          })
        }
      }
    }

    const text = ({ name, validate, type = 'text', defaultValue = '', title, validateOnBlur, integerValue, ...props }) => {
      const value = convertValue(_.get(model, name, defaultValue), type, integerValue)
      let validateResult = null
      if (validate) {
        validateResult = validateField({ name, value, validate, model, setModel, initialState, type })
      } else if(_.get(model, `errors['${name}']`)) {
        _.unset(model, `errors['${name}']`)
        const result = { ...model }
        result.isValid = Reflect.ownKeys(result.errors || {}).length === 0
        setModel(result)
      }

      return {
        onChange,
        ...(type === 'select' ? { 'data-integer': integerValue } : null),
        name,
        value,
        type,
        error: validateResult || _.get(model, `errors['${name}']`),
        ...(title && ['image', 'list', 'checkbox'].includes(type) ? { title } : null),
        ...props,
      }
    }

    const list = params => text({ ...params, type: 'list', defaultValue: [] })

    const number = params => text({ ...params, type: 'number', defaultValue: 0 })

    const image = params => text({ ...params, type: 'image' })

    const select = params => text({ ...params, type: 'select' })

    const checkbox = params => text({ ...params, defaultValue: false, type: 'checkbox' })

    return {
      text,
      number,
      image,
      select,
      checkbox,
      list,
      setModel,
      onChange,
      batchChange,
      reset,
      resetDirty,
      isValidPath,
      cleanErrorsPath,
      model,
      values: model.values,
      errors: model.errors,
      isValid: model.isValid,
      isDirty: model.isDirty,
      initialState,
    }
  }, [initialState, model, initialStateValuesJSON])

  return modelProps
}

export default useModel
